import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/update-password")({
  head: () => ({
    meta: [
      { title: "Update Password — The Hospitality Reserve" },
      { name: "description", content: "Set a new password for your Hospitality Reserve member account after a recovery email." },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/update-password" }],
  }),
  component: UpdatePasswordPage,
});

function UpdatePasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    if (params.get("type") === "recovery" || params.get("access_token")) {
      setValid(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. Please sign in.");
      navigate({ to: "/auth" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-8 py-6 lg:px-16">
        <Link to="/" className="inline-block">
          <p className="tracking-wide-luxury text-accent">The Hospitality</p>
          <p className="font-display text-lg">Reserve</p>
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="tracking-wide-luxury text-accent">New Password</p>
            <span className="gold-divider mt-6" />
            <h1 className="mt-6 font-display text-3xl">Set a new password</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Choose something secure and easy to remember.
            </p>
          </div>

          {!valid ? (
            <div className="bg-white text-foreground p-8 text-center">
              <p className="tracking-wide-luxury text-destructive mb-4">Invalid or expired link</p>
              <p className="text-sm text-muted-foreground">
                This password reset link is no longer valid. Please request a new one.
              </p>
              <div className="mt-6">
                <Link to="/reset-password" className="text-xs tracking-wide-luxury text-accent hover:text-accent/80 transition-colors">
                  Request new link
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 bg-white text-foreground p-8">
              <div>
                <Label htmlFor="password">New Password</Label>
                <PasswordInput id="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm Password</Label>
                <PasswordInput id="confirm" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6">
                {loading ? "Please wait…" : "Update Password"}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/auth" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors">
              Back to sign in
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
